import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@/stores/RootStore";
import type {
  SupportedLanguage,
  Prefecture,
  City,
  Location,
  Station,
  AddressValidationRequest,
  AddressValidationResult,
  GeocodeRequest,
  GeocodeResult,
} from "../types/geo.types";

export class GeoStore {
  readonly prefecturesByLocale: Map<SupportedLanguage, Prefecture[]> =
    new Map();
  readonly citiesByPrefecture: Map<string, City[]> = new Map(); // key: `${lang}:${prefectureCode}`
  readonly locationsByCity: Map<string, Location[]> = new Map(); // key: `${lang}:${citySlug ?? cityId}`
  readonly stationsByCity: Map<string, Station[]> = new Map(); // key: `${lang}:${citySlug ?? cityId}`
  readonly stationSearchResults: Map<string, Station[]> = new Map(); // key: `${lang}:${citySlug ?? 'all'}:${query}`

  loading: boolean = false;
  error: string | null = null;

  constructor(private root: RootStore) {
    makeAutoObservable(this, {
      root: false,
    } as any);
  }

  async loadPrefectures(lang: SupportedLanguage): Promise<Prefecture[]> {
    if (this.prefecturesByLocale.has(lang)) {
      return this.prefecturesByLocale.get(lang)!;
    }
    this.loading = true;
    try {
      const prefectures = await this.root.api.geo.fetchPrefectures(lang);
      runInAction(() => {
        this.prefecturesByLocale.set(lang, prefectures);
        this.loading = false;
      });
      return prefectures;
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message;
        this.loading = false;
      });
      return [];
    }
  }

  async loadCities(
    prefectureCode: number,
    lang: SupportedLanguage,
  ): Promise<City[]> {
    const key = `${lang}:${prefectureCode}`;
    if (this.citiesByPrefecture.has(key)) {
      return this.citiesByPrefecture.get(key)!;
    }
    this.loading = true;
    try {
      const cities = await this.root.api.geo.fetchCities(prefectureCode, lang);
      runInAction(() => {
        this.citiesByPrefecture.set(key, cities);
        this.loading = false;
      });
      return cities;
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message;
        this.loading = false;
      });
      return [];
    }
  }

  async loadLocations(options: {
    citySlug?: string;
    cityId?: string;
    lang: SupportedLanguage;
  }): Promise<Location[]> {
    const key = `${options.lang}:${options.citySlug ?? options.cityId}`;
    if (this.locationsByCity.has(key)) {
      return this.locationsByCity.get(key)!;
    }
    this.loading = true;
    try {
      const locations = await this.root.api.geo.fetchLocations(options);
      runInAction(() => {
        this.locationsByCity.set(key, locations);
        this.loading = false;
      });
      return locations;
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message;
        this.loading = false;
      });
      return [];
    }
  }

  async loadStations(options: {
    citySlug?: string;
    cityId?: string;
    locationId?: string;
    lang: SupportedLanguage;
  }): Promise<Station[]> {
    const key = `${options.lang}:${options.citySlug ?? options.cityId}`;
    if (this.stationsByCity.has(key)) {
      return this.stationsByCity.get(key)!;
    }
    this.loading = true;
    try {
      const stations = await this.root.api.geo.fetchStations(options);
      runInAction(() => {
        this.stationsByCity.set(key, stations);
        this.loading = false;
      });
      return stations;
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message;
        this.loading = false;
      });
      return [];
    }
  }
  async searchStations(
    query: string,
    options: {
      citySlug?: string;
      limit?: number;
      lang: SupportedLanguage;
    },
  ): Promise<Station[]> {
    const key = `${options.lang}:${options.citySlug ?? "all"}:${query}`;
    if (this.stationSearchResults.has(key)) {
      return this.stationSearchResults.get(key)!;
    }
    this.loading = true;
    try {
      const stations = await this.root.api.geo.searchStations(query, options);
      runInAction(() => {
        this.stationSearchResults.set(key, stations);
        this.loading = false;
      });
      return stations;
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message;
        this.loading = false;
      });
      return [];
    }
  }

  async findNearbyStations(
    lat: number,
    lon: number,
    options: {
      radiusM?: number;
      citySlug?: string;
      limit?: number;
      lang: SupportedLanguage;
    },
  ): Promise<Station[]> {
    this.loading = true;
    try {
      const stations = await this.root.api.geo.findNearbyStations(
        lat,
        lon,
        options,
      );
      runInAction(() => {
        this.loading = false;
      });
      return stations;
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message;
        this.loading = false;
      });
      return [];
    }
  }

  async validateAddress(
    request: AddressValidationRequest,
  ): Promise<AddressValidationResult> {
    this.loading = true;
    try {
      const result = await this.root.api.geo.validateAddress(request);
      runInAction(() => {
        this.loading = false;
      });
      return result;
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message;
        this.loading = false;
      });
      throw err;
    }
  }

  async geocodeAddress(request: GeocodeRequest): Promise<GeocodeResult> {
    this.loading = true;
    try {
      const result = await this.root.api.geo.geocodeAddress(request);
      runInAction(() => {
        this.loading = false;
      });
      return result;
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message;
        this.loading = false;
      });
      throw err;
    }
  }

  reset() {
    this.prefecturesByLocale.clear();
    this.citiesByPrefecture.clear();
    this.locationsByCity.clear();
    this.stationsByCity.clear();
    this.stationSearchResults.clear();
    this.error = null;
    this.loading = false;
  }
}
